import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStatus } from './guards/SubscriptionGuard';
import { enlaceWhatsApp } from '../contacto.js';
import './UserStatusDropdown.css';

/** «2026-10-11» leído al mediodía, para que ningún huso horario lo mueva de día. */
const fechaDe = (iso, opciones) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString('es-MX', opciones);

export default function UserStatusDropdown({ user, onLogout }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  
  // Extraemos la información del status en tiempo real (caché de 15s)
  const { status, plan, expiryDate, tokensLeft, tokensMax, xp, esEquipo, renueva,
    sinPlan, empresa, loading } = useSubscriptionStatus();

  // Cerrar al clickear afuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  // ── LOS TOKENS, SIN INVENTAR NINGUNO ────────────────────────────────────
  // Esto era `tokensMax > 0 ? tokensMax : 1000`: a quien no tiene plan —tope 0—
  // el menú le pintaba «0 / 1.000», un tope que no existe en ningún plan. Rob lo
  // vio el 11-09-2026 en una cuenta de alumno. Sin tope no hay fracción que
  // enseñar: se dice que no hay plan.
  const hayTope = Number(tokensMax) > 0;
  const maxT = hayTope ? Number(tokensMax) : 0;
  const leftT = hayTope ? Number(tokensLeft ?? 0) : 0;
  const pct = hayTope
    ? Math.max(0, Math.min(100, Math.round((leftT / maxT) * 100)))
    : 0;
  
  // Emoji según el porcentaje
  let tokenEmoji = "🔥";
  if (pct < 20) tokenEmoji = "🪫";
  else if (pct < 50) tokenEmoji = "🔋";
  else if (pct < 90) tokenEmoji = "⚡";

  // Fecha de vencimiento legible
  const formattedExpiry = expiryDate 
    ? new Date(expiryDate + "T12:00:00").toLocaleDateString('es-MX', { month: 'short', day: 'numeric', year: 'numeric' })
    : '∞';

  // Un profesor no vence: se le RENUEVAN los tokens cada mes desde su
  // inscripción. Su menú decía «∞» porque el servidor le daba el pase de
  // administrador (Rob lo vio el 11-09-2026).
  // ── EL EMPLEADO DE UNA EMPRESA TIENE DOS FECHAS, Y NO SON LO MISMO ──────
  // Rob, 18-09-2026, con un contrato de 4 meses delante: «dice Renovación 18
  // ene 2027… pienso, ¿en serio solo tengo 777 tokens para 4 meses? Si yo me
  // confundí, cualquier trabajador se puede confundir».
  //
  // Tenía razón: aquí se enseñaba el FIN DEL CONTRATO en el hueco de la
  // renovación. Su energía se renueva CADA MES —eso siempre funcionó—, pero el
  // menú contaba la otra fecha y parecía que 777 tokens tenían que durarle
  // hasta enero. Ahora son dos líneas distintas, con dos nombres distintos.
  const esEmpleado = Boolean(empresa?.nombre) && Boolean(renueva);

  const renovacion = (esEquipo || esEmpleado)
    ? (renueva ? fechaDe(renueva, { month: 'short', day: 'numeric', year: 'numeric' }) : '—')
    // Sin plan no hay nada que renovar: «∞» decía justo lo contrario de lo que
    // pasa, que es que esa cuenta no tiene acceso.
    : sinPlan ? '—'
      : formattedExpiry;

  const cubiertoHasta = esEmpleado && empresa?.vence
    ? fechaDe(empresa.vence, { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  // El `alias` de un profesor no es un apodo: ahí se guarda su ficha en JSON.
  // Usado tal cual, el saludo empezaría por «{"lang":…».
  const apodo = user?.alias && !String(user.alias).trim().startsWith('{') ? user.alias : null;
  const initial = (apodo || user?.full_name || '?').charAt(0).toUpperCase();

  const statusUI = {
    active: { text: "Activo", icon: "✅", color: "var(--ok, #00ff88)" },
    tokens: { text: "Sin Tokens", icon: "⚠️", color: "var(--wrn, #ffaa00)" },
    paused: { text: "Suspendido", icon: "⏸️", color: "var(--tx2, #888888)" },
    overdue: { text: "Vencido", icon: "🔒", color: "var(--er, #ff5e7e)" },
    canceled: { text: "Cancelado", icon: "❌", color: "var(--er, #ff5e7e)" },
  };
  // Fallback de seguridad
  const currentUI = statusUI[status] || statusUI.active;

  // Un profesor no compra planes: se los asigna gerencia, y «Mejorar mi Plan» le
  // mandaba a la tienda de alumnos. Pedir más va por WhatsApp, como todo lo que
  // coordina gerencia, y el mensaje sale sin emojis (llegaban rotos).
  function pideMasTokens() {
    const nombre = user?.full_name || apodo || user?.email || 'Profesor';
    const mensaje = [
      '*Solicitud de tokens de IA*',
      '',
      `Profesor: ${nombre}`,
      user?.email ? `Correo: ${user.email}` : null,
      `Me quedan *${leftT.toLocaleString('es-MX')}* de ${maxT.toLocaleString('es-MX')} tokens de IA en este ciclo.`,
      renueva ? `Se renuevan el ${fechaDe(renueva, { day: 'numeric', month: 'long' })}.` : null,
      '',
      '¿Me pueden ampliar el saldo?',
    ].filter((linea) => linea !== null).join('\n');
    window.open(enlaceWhatsApp(mensaje), '_blank', 'noopener,noreferrer');
  }

  return (
    <div className="usd-container" ref={dropdownRef}>
      {/* Botón Avatar */}
      <button 
        className={`usd-avatar-btn ${isOpen ? 'usd-active' : ''}`} 
        onClick={toggleDropdown}
        aria-label="Menú de usuario"
      >
        <span className="usd-initial">{initial}</span>
        {/* Glow animado cuando hay tokens */}
        {pct > 0 && <div className="usd-avatar-glow" />}
      </button>

      {/* Menú Desplegable (Glassmorphism) */}
      <div className={`usd-dropdown ${isOpen ? 'usd-dropdown-open' : ''}`}>
        
        {/* Header del dropdown */}
        <div className="usd-header">
          <div className="usd-greeting">
            Hola, <span className="usd-name">{apodo || user?.full_name?.split(' ')[0]}</span> 👋
          </div>
          <div className="usd-badges">
            <div className="usd-plan-badge">
              {sinPlan ? "⚠️" : "💎"} {plan || "Plan Base"}
            </div>
            <div className="usd-xp-badge" title="Puntos de Experiencia">
              🏆 {xp !== undefined ? xp.toLocaleString() : (user?.xp || 0).toLocaleString()} XP
            </div>
          </div>
        </div>

        <div className="usd-divider" />

        {/* Sección de Tokens */}
        <div className="usd-section">
          <div className="usd-section-title">
            <span>Energía Spacy disponible</span>
            <span>{tokenEmoji}</span>
          </div>
          
          <div className="usd-progress-wrap">
            <div className="usd-progress-bg">
              <div 
                className="usd-progress-fill" 
                style={{ 
                  width: `${pct}%`,
                  background: pct < 20 ? 'var(--usd-er)' : pct < 50 ? 'var(--usd-wrn)' : 'var(--usd-ok)'
                }}
              />
            </div>
          </div>
          
          <div className="usd-stats">
            <span className="usd-pct" style={{ color: pct < 20 ? 'var(--usd-er)' : 'inherit' }}>
              {pct}%
            </span>
            <span className="usd-fraction">
              {loading ? "..."
                : hayTope ? `${leftT.toLocaleString()} / ${maxT.toLocaleString()}`
                : "Sin plan"}
            </span>
          </div>
        </div>

        {/* Sección de Estado */}
        <div className="usd-section usd-info-grid">
          <div className="usd-info-box">
            <div className="usd-info-label">Estado</div>
            <div className="usd-info-val">
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>{currentUI.icon}</span>
                <span style={{ color: currentUI.color, fontWeight: 'bold' }}>
                  {currentUI.text}
                </span>
              </div>
            </div>
          </div>
          <div className="usd-info-box">
            <div className="usd-info-label">
              {esEmpleado ? 'Tus tokens se renuevan' : 'Renovación'}
            </div>
            <div className="usd-info-val">{renovacion}</div>
          </div>
        </div>

        {/* La otra fecha, dicha aparte y con su nombre: hasta cuándo la cubre su
            empresa. Juntarlas en una sola línea fue lo que confundió. */}
        {cubiertoHasta ? (
          <div className="usd-empresa-pie" data-prueba="cubierto-hasta">
            {empresa.nombre} te cubre hasta el {cubiertoHasta}
          </div>
        ) : null}

        <div className="usd-divider" />

        {/* ── RECARGAR, CUANDO LE QUEDA POCA (20-09-2026) ──────────────────
            Rob: «cuando un alumno está a punto de acabarse sus tokens o
            terminados, pueda recargar conforme a su plan».

            Aparece SOLO por debajo del 40 %: ofrecer una recarga a quien va
            por el 80 % de su energía es vender por vender, y el alumno aprende
            a ignorar el botón justo antes de necesitarlo de verdad. */}
        {!esEquipo && hayTope && pct < 40 && (
          <button
            className="usd-recarga-btn"
            data-prueba="menu-recargar"
            onClick={() => {
              setIsOpen(false);
              window.dispatchEvent(new Event('nsl:recargar'));
            }}
          >
            ⚡ {leftT <= 0 ? 'Recargar energía' : 'Te queda poca energía · recargar'}
          </button>
        )}

        {/* Footer actions */}
        <div className="usd-footer">
          <button 
            className="usd-upgrade-btn"
            onClick={() => {
              setIsOpen(false);
              if (esEquipo) pideMasTokens(); else navigate('/plans');
            }}
          >
            {esEquipo ? 'Pedir más tokens a gerencia'
              : (sinPlan || status === 'overdue') ? '⚡ Ver los planes'
                : '⭐ Mejorar mi Plan'}
          </button>
          
          <button 
            className="usd-logout-btn"
            onClick={() => { setIsOpen(false); onLogout(); }}
          >
            Cerrar Sesión
          </button>
        </div>
      </div>
    </div>
  );
}
